// RecoveriesPage — lists the most recent recovered payments.
// Data from GET /api/v1/recoveries (tenant-scoped, RLS-enforced by the backend).

import { useQuery } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { fetchRecoveries, type Recovery } from "../api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@avagency/frontend/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@avagency/frontend/components/ui/table";
import { EmptyState } from "@avagency/frontend/components/shell/EmptyState";
import { ErrorState } from "@avagency/frontend/components/shell/ErrorState";
import { LoadingState } from "@avagency/frontend/components/shell/LoadingState";

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function RecoveriesPage() {
  const q = useQuery<Recovery[]>({
    queryKey: ["dundial", "recoveries"],
    queryFn: () => fetchRecoveries(100),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Recoveries</h1>
        <p className="mt-1 font-mono text-xs uppercase tracking-widest text-muted-foreground">
          Payments recovered after initial failure
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recovery history</CardTitle>
          <CardDescription>
            Most recent 100 recovered payments across all connected accounts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isLoading && <LoadingState rows={5} />}
          {q.error && <ErrorState error={q.error} retry={() => q.refetch()} />}
          {q.data && q.data.length === 0 && (
            <EmptyState
              icon={
                <div className="dundial-empty-glyph" aria-hidden>
                  <CheckCircle2 className="h-6 w-6 dundial-icon-ok" />
                </div>
              }
              title="No recoveries yet"
              description="Recovered payments will appear here once DunDial starts retrying failed charges."
            />
          )}
          {q.data && q.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recovered at</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Charge ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{formatDate(r.recovered_at)}</TableCell>
                    <TableCell className="font-medium">
                      {formatCents(r.recovered_amount_cents, r.currency)}
                    </TableCell>
                    <TableCell>
                      <span className="dundial-decline-chip">{r.source}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-[11px] text-muted-foreground/70">
                        {r.stripe_charge_id}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
